<script setup>
import { computed } from 'vue';
import { useSidebar } from 'dashboard/composables/useSidebar';
import {
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  TooltipContent,
} from 'reka-ui';

const props = defineProps({
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  tooltip: { type: String, default: '' },
  variant: { type: String, default: 'default' },
  size: { type: String, default: 'default' },
  asChild: { type: Boolean, default: false },
});

const { isMobile, state } = useSidebar();

const buttonClasses = computed(() => {
  const variantClasses = {
    default: '',
    outline: 'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))]',
  };

  const sizeClasses = {
    default: 'min-h-[40px] text-[15px]',
    sm: 'h-8 text-[13px]',
    lg: 'h-14 text-[15px] group-data-[collapsible=icon]:!p-0',
  };

  const baseCommon =
    'peer/menu-button flex w-full min-w-0 items-center gap-2.5 overflow-hidden rounded-md px-3.5 py-2.5 text-left text-[15px] outline-none ring-sidebar-ring transition-all duration-150 focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 group-data-[collapsible=icon]:!size-9 group-data-[collapsible=icon]:!p-2.5 cursor-pointer border border-transparent';

  let stateClasses = '';

  // Item actif (page sélectionnée) : fond clair, texte noir, icône bleue
  if (props.isActive) {
    stateClasses =
      'text-n-sidebar-text hover:bg-n-sidebar-item-active-hover hover:text-n-sidebar-text [&_svg]:text-n-sidebar-indicator';
  }
  // Item parent avec enfant actif : fond gris, texte noir, icône bleue
  else if (props.hasActiveChild) {
    stateClasses =
      'text-n-sidebar-text hover:bg-n-sidebar-item-active-hover hover:text-n-sidebar-text [&_svg]:text-n-sidebar-indicator';
  }
  // Item normal : pas de fond, hover bleu très clair, texte noir, icône bleue
  else {
    stateClasses =
      'text-n-sidebar-text hover:bg-n-sidebar-item-hover hover:text-n-sidebar-text [&_svg]:text-n-sidebar-indicator';
  }

  return `${baseCommon} ${variantClasses[props.variant]} ${sizeClasses[props.size]} ${stateClasses}`;
});

const showTooltip = computed(
  () => state.value === 'collapsed' && !isMobile.value && props.tooltip
);
</script>

<template>
  <TooltipProvider v-if="showTooltip" :delay-duration="0">
    <TooltipRoot>
      <TooltipTrigger as-child>
        <component
          :is="asChild ? 'div' : 'button'"
          data-sidebar="menu-button"
          :data-size="size"
          :data-active="isActive"
          :class="[
            buttonClasses,
            {
              'bg-n-sidebar-item-active': isActive,
              'bg-n-sidebar-parent-expanded': isExpanded,
              'bg-n-sidebar-parent-has-active-closed':
                hasActiveChild && !isExpanded && !isActive,
            },
          ]"
        >
          <slot />
        </component>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        class="bg-n-solid-1 text-n-slate-12 px-2.5 py-1.5 rounded-lg text-[15px] border border-n-weak"
      >
        {{ tooltip }}
      </TooltipContent>
    </TooltipRoot>
  </TooltipProvider>
  <component
    :is="asChild ? 'div' : 'button'"
    v-else
    data-sidebar="menu-button"
    :data-size="size"
    :data-active="isActive"
    :class="[
      buttonClasses,
      {
        'bg-n-sidebar-item-active': isActive,
        'bg-n-sidebar-parent-expanded': isExpanded,
        'bg-n-sidebar-parent-has-active-closed':
          hasActiveChild && !isExpanded && !isActive,
      },
    ]"
  >
    <slot />
  </component>
</template>
